import type { PublicApiTypes } from '@tf/api';
import type { TFEmptyStateMessageProps } from '@tf/ui';
import {
	Button,
	Center,
	Icon,
	TFCard,
	TFDataTable,
	TFEmptyStateMessage,
	TFLoadingOverlay,
	TFText,
} from '@tf/ui';

import { useDeleteTokenMutation, useTokens } from '@/core/api/tokens';

type CellProps = {
	row: {
		original: PublicApiTypes.TokenControllerListTokensData;
	};
};

export const TokensTable = () => {
	const { isLoading, data: tokens, isError } = useTokens();

	const deleteTokenMutation = useDeleteTokenMutation();

	if (isLoading) {
		return <TFLoadingOverlay h={300} label="Tokens loading..." />;
	}

	const errorMessage: TFEmptyStateMessageProps = {
		icon: <Icon.IconUnlink />,
		title: 'Error tokens loading',
		description: 'Please try again',
	};

	if (isError) {
		return (
			<TFCard>
				<Center h={300}>
					<TFEmptyStateMessage {...errorMessage} />
				</Center>
			</TFCard>
		);
	}

	return (
		<TFDataTable<PublicApiTypes.TokenControllerListTokensData>
			name="tokens-table"
			data={tokens ?? []}
			defs={[
				{
					header: 'Name',
					accessorKey: 'name',
					size: 150,
					sortingFn: (rowA, rowB) => {
						const rowAName = rowA.original.name;
						const rowBName = rowB.original.name;
						return rowAName.localeCompare(rowBName);
					},
					Cell: ({ row }: CellProps) => {
						const { name } = row.original;

						return (
							<TFText py=".25rem" lineClamp={1} inherit>
								{name}
							</TFText>
						);
					},
				},
				{
					header: 'Actions',
					accessorKey: 'actions',
					size: 100,
					enableSorting: false,
					enableResizing: false,
					Cell: ({ row }: CellProps) => {
						const { id } = row.original;
						return (
							<Button
								variant="light"
								color="red"
								disabled={deleteTokenMutation.isPending && deleteTokenMutation.variables?.id === id}
								onClick={async () => {
									await deleteTokenMutation.mutateAsync({ id });
								}}
								size={'xs'}
							>
								Revoke
							</Button>
						);
					},
				},
			]}
		/>
	);
};
